import CheckCircleBlack from '@public/icons/check-circle-black.svg';
import Chevron from '@public/icons/chev-down.svg';
import XCircleRed from '@public/icons/x-circle-red.svg';
import clsx from 'clsx';
import Image from 'next/image';

import { useToggle } from '@/hooks/useToggle';

type SystemFeature = {
  title: string;
  alterAnywhere: boolean | string;
  alterOne: boolean | string;
};

export type SystemFeaturesTablesProps = {
  features: {
    title: string;
    features: SystemFeature[];
  }[];
};

const SystemFeaturesTable = ({
  showHeader,
  title,
  features,
}: {
  showHeader: boolean;
  title: string;
  features: SystemFeature[];
}) => {
  return (
    <div className="border border-alter-black-20 rounded-lg">
      <table className="w-full">
        <thead>
          <tr className="border-b border-alter-black-20">
            <th className="text-left font-light">
              {!showHeader && (
                <p className="px-2 lg:px-10 py-5 uppercase font-bold text-xs lg:text-sm text-alter-black-80">
                  {title}
                </p>
              )}
            </th>
            <th className="text-center font-normal align-bottom lg:text-lg">
              <span
                className={clsx(
                  showHeader && 'py-4 block lg:pr-10',
                  !showHeader && 'invisible lg:pr-10'
                )}
              >
                Alter Anywhere
              </span>
            </th>
            <th className="text-center font-normal whitespace-nowrap align-bottom lg:text-lg">
              <span
                className={clsx(
                  showHeader && 'py-4 block pr-4 lg:pr-10',
                  !showHeader && 'invisible pr-4 lg:pr-10'
                )}
              >
                Alter One
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {showHeader && (
            <tr className="border-b border-alter-black-20">
              <td colSpan={3} className="px-2 py-5 lg:px-10">
                <p className="uppercase font-bold text-xs lg:text-sm text-alter-black-80">
                  {title}
                </p>
              </td>
            </tr>
          )}
          {features.map((feature, featureIndex) => (
            <tr key={feature.title} className="border-b border-alter-black-20 last:border-b-0">
              <td className="font-light text-sm lg:text-lg px-2 lg:px-10">{feature.title}</td>
              <td className="text-center py-4 lg:pr-10">
                {typeof feature.alterAnywhere === 'string' ? (
                  <span className="font-light">{feature.alterAnywhere}</span>
                ) : (
                  <span className="flex items-center justify-center">
                    {feature.alterAnywhere ? (
                      <Image src={CheckCircleBlack} width={24} height={24} alt="Feature included" />
                    ) : (
                      <Image src={XCircleRed} width={24} height={24} alt="Feature not included" />
                    )}
                  </span>
                )}
              </td>
              <td className="text-center py-4 lg:pr-10">
                {typeof feature.alterOne === 'string' ? (
                  <span className="font-light">{feature.alterOne}</span>
                ) : (
                  <span className="flex items-center justify-center">
                    {feature.alterOne ? (
                      <Image src={CheckCircleBlack} width={24} height={24} alt="Feature included" />
                    ) : (
                      <Image src={XCircleRed} width={24} height={24} alt="Feature not included" />
                    )}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SystemFeaturesTables = ({ features }: SystemFeaturesTablesProps) => {
  const { value: showingIncluded, toggle } = useToggle(true);

  return (
    <div className="flex flex-col gap-8 lg:gap-10">
      <button
        onClick={toggle}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-alter-black-20 w-min"
      >
        <span className="text-base whitespace-nowrap">What&apos;s Included</span>
        <Image
          src={Chevron}
          alt={showingIncluded ? 'Chevron down' : 'Chevron up'}
          className={clsx(
            'mr-4 transition-transform duration-200',
            showingIncluded && '-rotate-180'
          )}
        />
      </button>

      <div
        className={clsx(
          'flex flex-col gap-4 transition-all duration-200',
          showingIncluded ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden'
        )}
      >
        {features.map((feature, index) => (
          <SystemFeaturesTable
            showHeader={index === 0}
            title={feature.title}
            features={feature.features}
            key={index}
          />
        ))}
        <p className="font-light text-sm text-alter-black-80">
          * Membership comes included for first 12-24 months, then goes to $39 to $59 depending on
          individual or family pricing. All monthly membership fees are waived when you complete
          your weekly Alter plan each month
        </p>
      </div>
    </div>
  );
};

export default SystemFeaturesTables;
